/**
 * Email Signal Engine - Front-end only
 * Mock data + LocalStorage for threads, KPIs, risk, interventions, huddle brief.
 * No backend; simulates Layer 1–4 for UI development.
 */

const STORAGE_KEYS = {
  ORG: 'nexonurse_email_connector_org',
  THREADS: 'nexonurse_email_threads',
  CONFIG: 'nexonurse_email_config',
  HUDDLE: 'nexonurse_email_huddle',
  FEATURE: 'nexonurse_feature_email_signal',
  QUEUE_RESOLVED: 'nexonurse_email_queue_resolved',
  PRIORITY_QUEUE_THREAD_IDS: 'nexonurse_email_priority_queue_thread_ids',
};

const CATEGORIES = ['Supply Chain', 'Staffing', 'Scheduling', 'Equipment', 'Quality', 'Facilities'];
const DEFAULT_RISK_WEIGHTS = { aging: 25, latency: 25, escalation: 30, loopDensity: 20 };
const DEFAULT_TIERS = { low: 30, moderate: 60, high: 80 };

function getOrg() {
  const raw = localStorage.getItem(STORAGE_KEYS.ORG);
  if (raw) return JSON.parse(raw);
  return {
    id: 'org-1',
    name: 'Regional Hospital',
    ingestionEmail: '',
    connectorStatus: 'not_setup', // not_setup | calibrating | active
    calibratedAt: null,
  };
}

function setOrg(org) {
  localStorage.setItem(STORAGE_KEYS.ORG, JSON.stringify(org));
}

function getConfig() {
  const raw = localStorage.getItem(STORAGE_KEYS.CONFIG);
  if (raw) return JSON.parse(raw);
  return {
    riskWeights: DEFAULT_RISK_WEIGHTS,
    tierThresholds: DEFAULT_TIERS,
    categoryOwnerMap: CATEGORIES.reduce((acc, c) => ({ ...acc, [c]: null }), {}),
    huddleTime: '06:00',
    huddleRecipients: [],
  };
}

function setConfig(config) {
  localStorage.setItem(STORAGE_KEYS.CONFIG, JSON.stringify(config));
}

function getThreads() {
  const raw = localStorage.getItem(STORAGE_KEYS.THREADS);
  if (raw) return JSON.parse(raw);
  return [];
}

function setThreads(threads) {
  localStorage.setItem(STORAGE_KEYS.THREADS, JSON.stringify(threads));
}

// --- Feature toggle ---
function getFeatureToggle() {
  const raw = localStorage.getItem(STORAGE_KEYS.FEATURE);
  return raw ? JSON.parse(raw) : { enabled: false, enabledForRoles: [] };
}

function setFeatureToggle(toggle) {
  localStorage.setItem(STORAGE_KEYS.FEATURE, JSON.stringify(toggle));
}

function isFeatureEnabled(role) {
  const t = getFeatureToggle();
  if (t.enabled) return true;
  return (t.enabledForRoles || []).includes(role);
}

// --- Mock thread + risk data ---
function generateMockThreads() {
  const now = new Date();
  const threads = [
    {
      id: 'thread-1',
      category: 'Supply Chain',
      subjectMasked: 'Re: Supply request #347',
      state: 'stalled',
      riskScore: 78,
      riskTier: 'high',
      explanation: 'This supply chain thread has been idle for 3 days (2.4x category baseline), involves 4 unique actors with no clear owner, the CC list expanded to include a director 6 hours ago (escalation signal), and loop density is 2.1x the category average.',
      factors: { aging: 1.8, latency: 1.2, escalation: 1.5, loopDensity: 2.1 },
      createdAt: new Date(now - 5 * 24 * 60 * 60 * 1000).toISOString(),
      lastEventAt: new Date(now - 3 * 24 * 60 * 60 * 1000).toISOString(),
      uniqueActors: 4,
      replyCycles: 7,
      escalationSignals: ['CC expansion to leadership'],
      recommendedAction: 'Route to Supply Chain Lead for ownership and resolution',
      suggestedOwner: 'Supply Chain Lead',
    },
    {
      id: 'thread-2',
      category: 'Staffing',
      subjectMasked: 'URGENT — PM shift coverage',
      state: 'escalating',
      riskScore: 85,
      riskTier: 'critical',
      explanation: 'This staffing thread has urgency markers in the subject, 6 replies in the last 2 hours after 2 days idle (frequency acceleration), and is 1.8x category baseline for loop density.',
      factors: { aging: 1.2, latency: 2.0, escalation: 2.0, loopDensity: 1.8 },
      createdAt: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
      lastEventAt: new Date(now - 0.5 * 60 * 60 * 1000).toISOString(),
      uniqueActors: 3,
      replyCycles: 6,
      escalationSignals: ['Subject urgency', 'Frequency acceleration'],
      recommendedAction: 'Immediate escalation with draft message',
      suggestedOwner: 'Nurse Manager',
    },
    {
      id: 'thread-3',
      category: 'Scheduling',
      subjectMasked: 'Re: Re: Schedule change request',
      state: 'active',
      riskScore: 45,
      riskTier: 'moderate',
      explanation: 'Thread age is within baseline; latency variance is slightly elevated for one actor (1.3x). Consider monitoring.',
      factors: { aging: 0.9, latency: 1.3, escalation: 0, loopDensity: 1.1 },
      createdAt: new Date(now - 1 * 24 * 60 * 60 * 1000).toISOString(),
      lastEventAt: new Date(now - 2 * 60 * 60 * 1000).toISOString(),
      uniqueActors: 2,
      replyCycles: 3,
      escalationSignals: [],
      recommendedAction: 'Monitor or route to category owner',
      suggestedOwner: 'Scheduling Lead',
    },
    {
      id: 'thread-4',
      category: 'Equipment',
      subjectMasked: 'Equipment maintenance — Building A',
      state: 'idle',
      riskScore: 62,
      riskTier: 'high',
      explanation: 'Equipment thread idle 36 hours (1.6x baseline). Loop density 2.0x category average — multiple handoffs without resolution.',
      factors: { aging: 1.6, latency: 1.0, escalation: 0.5, loopDensity: 2.0 },
      createdAt: new Date(now - 4 * 24 * 60 * 60 * 1000).toISOString(),
      lastEventAt: new Date(now - 36 * 60 * 60 * 1000).toISOString(),
      uniqueActors: 5,
      replyCycles: 8,
      escalationSignals: [],
      recommendedAction: 'Consolidate ownership to single actor',
      suggestedOwner: 'Facilities Lead',
    },
    {
      id: 'thread-5',
      category: 'Quality',
      subjectMasked: 'Re: HCAHPS follow-up',
      state: 'active',
      riskScore: 28,
      riskTier: 'low',
      explanation: 'Thread within normal parameters. No action needed.',
      factors: { aging: 0.7, latency: 0.9, escalation: 0, loopDensity: 0.8 },
      createdAt: new Date(now - 12 * 60 * 60 * 1000).toISOString(),
      lastEventAt: new Date(now - 1 * 60 * 60 * 1000).toISOString(),
      uniqueActors: 2,
      replyCycles: 2,
      escalationSignals: [],
      recommendedAction: 'None',
      suggestedOwner: 'Quality Lead',
    },
  ];
  setThreads(threads);
  return threads;
}

function ensureMockData() {
  let threads = getThreads();
  if (threads.length === 0) threads = generateMockThreads();
  return threads;
}

// --- KPI summaries (derived from threads for UI) ---
function getAgingSummary() {
  const threads = ensureMockData();
  const byCategory = {};
  CATEGORIES.forEach(c => {
    byCategory[c] = { p50: 24, p75: 48, p90: 72, count: threads.filter(t => t.category === c).length };
  });
  threads.forEach(t => {
    if (!byCategory[t.category]) byCategory[t.category] = { p50: 24, p75: 48, p90: 72, count: 0 };
    byCategory[t.category].count++;
  });
  return byCategory;
}

function getLatencyByActor() {
  return [
    { actorHash: 'usr-1', displayName: 'Team Member A', meanHours: 4.2, stdDev: 1.2, deviation: false },
    { actorHash: 'usr-2', displayName: 'Team Member B', meanHours: 12.1, stdDev: 8.0, deviation: true },
    { actorHash: 'usr-3', displayName: 'Team Member C', meanHours: 2.8, stdDev: 0.9, deviation: false },
  ];
}

function getEscalationFeed() {
  const threads = ensureMockData();
  const feed = [];
  threads.forEach(t => {
    t.escalationSignals.forEach((sig, i) => {
      feed.push({
        id: `${t.id}-esc-${i}`,
        threadId: t.id,
        category: t.category,
        signalType: sig,
        at: t.lastEventAt,
      });
    });
  });
  feed.sort((a, b) => new Date(b.at) - new Date(a.at));
  return feed.slice(0, 20);
}

function getLoopDensityByCategory() {
  const threads = ensureMockData();
  const byCat = {};
  CATEGORIES.forEach(c => {
    const list = threads.filter(t => t.category === c);
    const avg = list.length ? list.reduce((s, t) => s + t.replyCycles, 0) / list.length : 0;
    byCat[c] = { avgLoops: Math.round(avg * 10) / 10, baseline: 3.0, count: list.length };
  });
  return byCat;
}

function getResolvedDismissedIds() {
  const raw = localStorage.getItem(STORAGE_KEYS.QUEUE_RESOLVED);
  return raw ? JSON.parse(raw) : { resolved: [], dismissed: [] };
}

function setResolvedDismissedIds(ids) {
  localStorage.setItem(STORAGE_KEYS.QUEUE_RESOLVED, JSON.stringify(ids));
}

// --- Intervention queue (risk >= 61) ---
function getInterventionQueue() {
  const threads = ensureMockData();
  const ids = getResolvedDismissedIds();
  const highThreshold = 61;
  return threads
    .filter(t => t.riskScore >= highThreshold && !ids.resolved.includes(t.id) && !ids.dismissed.includes(t.id))
    .sort((a, b) => b.riskScore - a.riskScore)
    .map(t => ({ ...t, queueStatus: 'pending' }));
}

function resolveIntervention(threadId) {
  const ids = getResolvedDismissedIds();
  ids.resolved.push(threadId);
  setResolvedDismissedIds(ids);
}

function dismissIntervention(threadId) {
  const ids = getResolvedDismissedIds();
  ids.dismissed.push(threadId);
  setResolvedDismissedIds(ids);
}

// --- Priority Queue (threads added from Morning Brief) ---
function getPriorityQueueThreadIds() {
  const raw = localStorage.getItem(STORAGE_KEYS.PRIORITY_QUEUE_THREAD_IDS);
  if (!raw) return [];
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function addThreadToPriorityQueueTracking(threadId) {
  const ids = getPriorityQueueThreadIds();
  if (ids.includes(threadId)) return;
  ids.push(threadId);
  localStorage.setItem(STORAGE_KEYS.PRIORITY_QUEUE_THREAD_IDS, JSON.stringify(ids));
}

// --- Draft message template ---
function getDraftMessage(threadId, actionType = 'escalate') {
  const threads = getThreads().length ? getThreads() : generateMockThreads();
  const t = threads.find(x => x.id === threadId) || threads[0];
  const hoursAgo = Math.round((Date.now() - new Date(t.lastEventAt).getTime()) / (60 * 60 * 1000));
  const subject = `Action Required — ${t.category} Thread Stalling`;
  const body = `Hi [Director Name],

I'm flagging a ${t.category.toLowerCase()} coordination thread that has been active without resolution. The thread involves ${t.uniqueActors} team members, has gone through ${t.replyCycles} reply cycles, and the last response was ${hoursAgo} hours ago. Based on our category norms, this thread is significantly overdue for closure.

Could you help assign clear ownership or provide direction on next steps?

Thank you,
[COM Name]`;
  return { subject, body, suggestedRecipients: [t.suggestedOwner] };
}

// --- Huddle brief ---
function getHuddleToday() {
  const threads = ensureMockData();
  const top = [...threads].sort((a, b) => b.riskScore - a.riskScore).slice(0, 5);
  const latencyTrend = { direction: 'stable', value: 4.2, unit: 'hours', period: '7 days' };
  const categoryCongestion = CATEGORIES.map(c => ({
    name: c,
    volume: threads.filter(t => t.category === c).length,
    avgAgeHours: 24 + Math.random() * 48,
    escalationCount: threads.filter(t => t.category === c && t.escalationSignals.length).length,
  }));
  const actorWorkload = getLatencyByActor().filter(a => a.deviation);
  return {
    briefDate: new Date().toISOString().split('T')[0],
    topAtRisk: top,
    latencyTrend,
    categoryCongestion,
    actorWorkload,
    recommendedFocus: top[0] ? `Address: ${top[0].subjectMasked} (Risk ${top[0].riskScore})` : 'No critical threads.',
  };
}

// --- Connector setup ---
function setupConnector() {
  const org = getOrg();
  org.ingestionEmail = `ingest-${org.id}@nexonurse.com`;
  org.connectorStatus = 'calibrating';
  org.calibratedAt = null;
  setOrg(org);
  return org;
}

function getITHandoffData() {
  const org = getOrg();
  return {
    ingestionEmail: org.ingestionEmail || `ingest-${org.id}@nexonurse.com`,
    recommendedLists: ['Operations DL', 'Supply Chain', 'Staffing', 'Scheduling', 'Facilities', 'Quality'],
    mailRuleInstructions: 'Configure a mail flow rule to BCC all messages to/from the above lists to the ingestion address. Do not store body or attachments.',
  };
}

export const EmailSignalEngine = {
  STORAGE_KEYS,
  getOrg,
  setOrg,
  getConfig,
  setConfig,
  getThreads,
  setThreads,
  getResolvedDismissedIds,
  setResolvedDismissedIds,
  getFeatureToggle,
  setFeatureToggle,
  isFeatureEnabled,
  ensureMockData,
  generateMockThreads,
  getAgingSummary,
  getLatencyByActor,
  getEscalationFeed,
  getLoopDensityByCategory,
  getInterventionQueue,
  resolveIntervention,
  dismissIntervention,
  getPriorityQueueThreadIds,
  addThreadToPriorityQueueTracking,
  getDraftMessage,
  getHuddleToday,
  setupConnector,
  getITHandoffData,
  CATEGORIES,
};
